import React, { useState } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Shield, Lock, User } from 'lucide-react';
import { PasswordInput } from '../ui/password-input';

const AdminLoginPage = () => {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState('fr');

  const t = {
    fr: {
      title: "Back-Office O'SCOP",
      subtitle: "Gestion des règles du simulateur coopératif",
      email: "Email",
      password: "Mot de passe",
      login: "Se connecter",
      loading: "Connexion...",
      error: "Erreur de connexion",
      legal: "Accès réservé aux administrateurs autorisés"
    },
    en: {
      title: "O'SCOP Back-Office",
      subtitle: "Cooperative simulator rules management",
      email: "Email",
      password: "Password",
      login: "Sign in",
      loading: "Signing in...",
      error: "Login error",
      legal: "Access restricted to authorized administrators"
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4">
      {/* Language toggle */}
      <div className="absolute top-4 right-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
          className="text-white/70 hover:text-white"
        >
          {lang === 'fr' ? '🇬🇧 EN' : '🇫🇷 FR'}
        </Button>
      </div>

      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur">
        <CardHeader className="text-center space-y-2 pb-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-2">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800">
            {t[lang].title}
          </CardTitle>
          <CardDescription className="text-gray-500">
            {t[lang].subtitle}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">
                <User className="w-4 h-4 inline mr-2" />
                {t[lang].email}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@oscop.fr"
                required
                className="h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">
                <Lock className="w-4 h-4 inline mr-2" />
                {t[lang].password}
              </Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-11"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t[lang].loading}
                </>
              ) : (
                t[lang].login
              )}
            </Button>
          </form>
          
          <p className="text-xs text-center text-gray-400 mt-4">
            🔒 {t[lang].legal}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
